/**
 * Universal Clinical Laboratory Barcode Check-Digit Utility
 * Standardized across frontend preview, barcode generation, and scanner validation.
 */

/**
 * Calculates a check digit based on the specified algorithm.
 * @param {string|number} baseIdentifier - The base alphanumeric or numeric identifier.
 * @param {string} algorithm - 'LUHN' | 'MOD10' | 'MOD11' | 'ISO7064' | 'NONE'
 * @returns {string} The computed check digit character or empty string if 'NONE'.
 */
export function calculateCheckDigit(baseIdentifier, algorithm = 'MOD10') {
  if (!baseIdentifier) return '';
  const algo = (algorithm || 'MOD10').toUpperCase();
  if (algo === 'NONE') return '';

  // Extract only digits from string for numeric checksums
  const cleaned = String(baseIdentifier).replace(/\D/g, '');
  if (!cleaned) return '0';

  switch (algo) {
    case 'LUHN': {
      // Standard Luhn (Mod 10 with 2x, 1x alternating weighting from right to left)
      let sum = 0;
      let shouldDouble = true;
      for (let i = cleaned.length - 1; i >= 0; i--) {
        let digit = parseInt(cleaned.charAt(i), 10);
        if (shouldDouble) {
          digit *= 2;
          if (digit > 9) digit -= 9;
        }
        sum += digit;
        shouldDouble = !shouldDouble;
      }
      const remainder = sum % 10;
      return String(remainder === 0 ? 0 : 10 - remainder);
    }

    case 'MOD10': {
      // Standard Non-Weighted Modulo 10 sum of digits
      let sum = 0;
      for (let i = 0; i < cleaned.length; i++) {
        sum += parseInt(cleaned.charAt(i), 10);
      }
      const remainder = sum % 10;
      return String(remainder === 0 ? 0 : 10 - remainder);
    }

    case 'MOD11': {
      // Weighted Modulo 11 (Weights 2, 3, 4, 5, 6, 7 repeating from right to left)
      let sum = 0;
      let weight = 2;
      for (let i = cleaned.length - 1; i >= 0; i--) {
        sum += parseInt(cleaned.charAt(i), 10) * weight;
        weight = weight >= 7 ? 2 : weight + 1;
      }
      const remainder = sum % 11;
      const checkVal = 11 - remainder;
      if (checkVal === 11) return '0';
      if (checkVal === 10) return 'X';
      return String(checkVal);
    }

    case 'ISO7064': {
      // ISO 7064 Pure System Mod 37, 36 Checksum
      const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let check = 36;
      const inputStr = String(baseIdentifier).toUpperCase().replace(/[^0-9A-Z]/g, '');
      for (let i = 0; i < inputStr.length; i++) {
        const val = chars.indexOf(inputStr.charAt(i));
        if (val === -1) continue;
        check = (((check === 0 ? 36 : check) + val) * 2) % 37;
      }
      const checkIndex = (38 - check) % 37;
      return chars.charAt(checkIndex) || '0';
    }

    default:
      return '';
  }
}

/**
 * Formats a full barcode string with calculated check digit.
 * @param {string} prefix - e.g. 'MB', 'PED', 'BC'
 * @param {string|number} sequence - e.g. '202608492'
 * @param {string} algorithm - 'LUHN' | 'MOD10' | 'MOD11' | 'ISO7064' | 'NONE'
 * @returns {string} Formatted barcode string, e.g. 'MB-2026-08492-7'
 */
export function formatBarcodeWithCheckDigit(prefix, sequence, algorithm = 'MOD10') {
  const cleanPrefix = (prefix || 'MB').toUpperCase();
  const cleanSeq = String(sequence || '202608492');
  const cd = calculateCheckDigit(cleanSeq, algorithm);
  
  if (!cd || algorithm === 'NONE') {
    return `${cleanPrefix}-${cleanSeq}`;
  }
  return `${cleanPrefix}-${cleanSeq}-${cd}`;
}

/**
 * Validates whether a barcode string contains a valid checksum according to the algorithm.
 * @param {string} fullBarcode - e.g. 'MB-2026-08492-7' or '2026084927'
 * @param {string} algorithm - 'LUHN' | 'MOD10' | 'MOD11' | 'ISO7064'
 * @returns {boolean} True if checksum is mathematically valid.
 */
export function validateBarcodeChecksum(fullBarcode, algorithm = 'MOD10') {
  if (!fullBarcode || algorithm === 'NONE') return true;
  
  const parts = String(fullBarcode).split('-');
  let baseIdentifier = '';
  let providedCheckDigit = '';

  if (parts.length >= 3) {
    // Format: PREFIX-SEQUENCE-CHECKDIGIT
    baseIdentifier = parts.slice(1, -1).join('');
    providedCheckDigit = parts[parts.length - 1];
  } else if (parts.length === 2 && isNaN(parts[0])) {
    // Format: PREFIX-SEQUENCECHECKDIGIT
    const seq = parts[1];
    baseIdentifier = seq.slice(0, -1);
    providedCheckDigit = seq.slice(-1);
  } else {
    const raw = String(fullBarcode).replace(/\D/g, '');
    if (raw.length < 2) return false;
    baseIdentifier = raw.slice(0, -1);
    providedCheckDigit = raw.slice(-1);
  }

  const expected = calculateCheckDigit(baseIdentifier, algorithm);
  return expected.toUpperCase() === providedCheckDigit.toUpperCase();
}
